import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { ApiService } from '@app/core/graphql/services/api.service'
import {
  CANCEL_BOOKING,
  CREATE_BOOKING,
  END_BOOKING,
  GET_BOOKABLE_AREAS,
  GET_BOOKINGS,
  GET_BOOKING_USAGE_REPORT,
  SEARCH_BOOKING_CANDIDATES,
  UPDATE_BOOKING,
} from '@app/query/booking.query'
import {
  BookableArea,
  Booking,
  BookingCandidate,
  BookingFilters,
  BookingUsageGroupBy,
  BookingUsageReport,
  CreateBookingPayload,
  UpdateBookingPayload,
} from '@app/models/booking.model'

/** What every booking operation hands back: `data` on success, `errorText` on refusal. */
export interface BookingCallResult<T> {
  success: boolean
  errorText?: string
  data?: T
}

/**
 * Portal-side room booking (YB-2073).
 *
 * A thin wrapper over PAPI: no business rules live here. Every refusal PAPI returns is a
 * machine-readable code in `errorText` (`booking_conflict`, `booking_allowance_exceeded`,
 * `booking_already_started`, …), so each call passes ERROR_KEY — ApiService's own toast then
 * resolves `BOOKINGS.ERRORS.<code>` instead of showing the raw code to an admin. Failures are
 * still returned to the caller rather than swallowed into empty results, because the grid has to
 * know an action was refused.
 *
 * `client_id` is injected by ApiService; nothing here passes it.
 */
@Injectable({
  providedIn: 'root',
})
export class BookingsService {
  /** PAPI's refusal code for "this would spend past the group's credit pool". */
  static readonly ALLOWANCE_EXCEEDED = 'booking_allowance_exceeded'

  /**
   * Refusals this feature answers with a dialog instead of a toast. An overspend is a question
   * for the admin ("this goes 2 credits past the pool — book it anyway?"), not a failure, and a
   * red toast followed by a confirm box for the same event looks broken. Every other refusal
   * still toasts normally.
   */
  private static readonly SILENT_ERRORS = [BookingsService.ALLOWANCE_EXCEEDED] as const

  /** ApiService appends PAPI's errorText, giving e.g. BOOKINGS.ERRORS.booking_conflict. */
  private readonly ERROR_KEY = 'BOOKINGS.ERRORS.'

  constructor(private API: ApiService) {}

  /** The operation name is also the key the envelope arrives under. */
  private unwrap<T>(operation: string): (response: unknown) => BookingCallResult<T> {
    return (response: unknown) => {
      const envelope = (response ?? {}) as Record<string, unknown>
      // ApiService already peels the named operation off the GraphQL payload for single-op
      // documents; the fallback keeps this working if that ever changes.
      const inner = envelope[operation] as BookingCallResult<T> | undefined
      const result = inner ?? (envelope as unknown as BookingCallResult<T>)
      return { success: !!result.success, errorText: result.errorText, data: result.data }
    }
  }

  getBookableAreas(eventId?: number | null): Observable<BookingCallResult<BookableArea[]>> {
    return this.API.privateQuery<unknown>(GET_BOOKABLE_AREAS, { event_id: eventId ?? null }, this.ERROR_KEY).pipe(map(this.unwrap<BookableArea[]>('get_bookable_areas')))
  }

  /**
   * People this room may be booked for, matched on name or phone.
   *
   * PAPI decrypts before it matches, so a first name, a fragment of either name, or a phone typed
   * with or without its separators all find the person — none of which `get_passes(search:)` can
   * do, since it compares the encrypted column for equality. It also applies create_booking's own
   * permission rule, so nothing offered here can come back refused as `booking_not_permitted`.
   */
  searchCandidates(areaId: number, search: string): Observable<BookingCallResult<BookingCandidate[]>> {
    return this.API.privateQuery<unknown>(SEARCH_BOOKING_CANDIDATES, { area_id: areaId, search, take: 25 }, this.ERROR_KEY).pipe(
      map(this.unwrap<BookingCandidate[]>('search_booking_candidates'))
    )
  }

  /**
   * `from_date` / `to_date` are an overlap window, not a start-time range: PAPI returns every
   * booking whose window intersects it, so a meeting that started yesterday and is still running
   * shows up on today's view.
   */
  getBookings(from: Date, to: Date, filters: BookingFilters = {}): Observable<BookingCallResult<Booking[]>> {
    return this.API.privateQuery<unknown>(
      GET_BOOKINGS,
      {
        from_date: from.toISOString(),
        to_date: to.toISOString(),
        event_id: filters.event_id ?? null,
        area_ids: filters.area_ids?.length ? filters.area_ids : null,
        status: filters.status ?? null,
      },
      this.ERROR_KEY
    ).pipe(map(this.unwrap<Booking[]>('get_bookings')))
  }

  getUsageReport(groupBy: BookingUsageGroupBy, from: Date, to: Date, eventId?: number | null): Observable<BookingCallResult<BookingUsageReport>> {
    return this.API.privateQuery<unknown>(
      GET_BOOKING_USAGE_REPORT,
      {
        group_by: groupBy,
        from_date: from.toISOString(),
        to_date: to.toISOString(),
        event_id: eventId ?? null,
      },
      this.ERROR_KEY
    ).pipe(map(this.unwrap<BookingUsageReport>('get_booking_usage_report')))
  }

  create(payload: CreateBookingPayload): Observable<BookingCallResult<Booking>> {
    // vars-as-function so the auto-injected client_id lands INSIDE the input object, where
    // CreateBookingInput expects it, instead of as a stray top-level variable.
    return this.API._wrap<unknown>(
      'mutate',
      CREATE_BOOKING,
      (vars: Record<string, unknown>) => ({ input: { ...payload, client_id: vars['client_id'] } }),
      this.ERROR_KEY,
      BookingsService.SILENT_ERRORS
    ).pipe(map(this.unwrap<Booking>('create_booking')))
  }

  /**
   * Only the keys present on `payload` are sent. PAPI reads an explicit null as "clear this
   * field", so spreading a fully-populated object would make an extend-only edit erase the title.
   */
  update(bookingId: number, payload: UpdateBookingPayload): Observable<BookingCallResult<Booking>> {
    return this.API.privateMutation<unknown>(UPDATE_BOOKING, { booking_id: bookingId, input: payload }, this.ERROR_KEY, BookingsService.SILENT_ERRORS).pipe(
      map(this.unwrap<Booking>('update_booking'))
    )
  }

  /** Portal cancel ALWAYS refunds in full, whatever the room's cutoff — PAPI enforces that. */
  cancel(bookingId: number, reason?: string): Observable<BookingCallResult<Booking>> {
    return this.API.privateMutation<unknown>(CANCEL_BOOKING, { booking_id: bookingId, reason: reason ?? null }, this.ERROR_KEY).pipe(map(this.unwrap<Booking>('cancel_booking')))
  }

  /** "Evict" — free a room that is in use right now. Writes a booking_end log and revokes the grant. */
  end(bookingId: number): Observable<BookingCallResult<Booking>> {
    return this.API.privateMutation<unknown>(END_BOOKING, { booking_id: bookingId }, this.ERROR_KEY).pipe(map(this.unwrap<Booking>('end_booking')))
  }
}
